import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import './ChatbotStyles.scss';
import { InfoService } from '../../../services/site/InfoService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 }
  }
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  variant?: any;
}> = ({ children, className = '', variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_chatbot-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_chatbot-hero-title">
                Intelligent Chatbot Solutions
              </h1>
              <h2 className="subtitle is-4 sds_chatbot-hero-subtitle mb-5">
                24/7 Customer Engagement with AI-Powered Conversations
              </h2>
              <div className="content is-size-5 sds_chatbot-hero-description">
                <p className="mb-4">
                  <strong>What is an AI Chatbot?</strong> It's an intelligent virtual assistant that engages 
                  with your customers in natural conversations, answers questions instantly, captures leads, 
                  and provides support 24/7 - all without human intervention.
                </p>
                <p className="mb-4">
                  Imagine having a tireless assistant who never sleeps, speaks multiple languages, and knows 
                  everything about your business. Our chatbots provide instant support, qualify leads, and 
                  guide visitors through your sales process automatically.
                </p>
              </div>
              <div className="sds_chatbot-feature-badges">
                <span className="tag is-large sds_chatbot-badge">💬 24/7 Available</span>
                <span className="tag is-large sds_chatbot-badge">🌍 Multi-Language</span>
                <span className="tag is-large sds_chatbot-badge">🎯 Lead Generation</span>
                <span className="tag is-large sds_chatbot-badge">⚡ Instant Response</span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_chatbot-hero-visual"
            >
              <div className="sds_chatbot-animation">
                <motion.div 
                  className="sds_chatbot-bot-icon"
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  🤖
                </motion.div>
                <div className="sds_chatbot-chat-bubbles">
                  {['💬', '❓', '✅', '👍'].map((emoji, i) => (
                    <motion.div
                      key={i}
                      className="sds_chatbot-chat-bubble"
                      animate={{ 
                        y: [-10, 10, -10],
                        opacity: [0.7, 1, 0.7]
                      }}
                      transition={{ 
                        duration: 2 + i * 0.3, 
                        repeat: Infinity,
                        delay: i * 0.2 
                      }}
                    >
                      {emoji}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Video Placeholder */}
        <div className="sds_chatbot-video-container mt-6">
          <motion.div 
            className="sds_chatbot-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">What is an AI Chatbot?</p>
            <p className="mt-2">See how chatbots transform customer engagement</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Drag & Drop Builder Section
const DragDropBuilderSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_chatbot-builder section">
      <div className="container">
        <div className="box sds_chatbot-builder-box">
          <h3 className="title is-3 has-text-centered mb-5">
            🎯 Easy Drag & Drop Chatbot Builder
          </h3>
          <p className="subtitle is-5 has-text-centered mb-5">No Coding Required!</p>
          
          <div className="sds_chatbot-builder-flow">
            <motion.div 
              className="sds_chatbot-flow-item"
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <span className="sds_chatbot-flow-icon">👋</span>
              <p className="has-text-weight-bold">Welcome Message</p>
            </motion.div>
            
            <motion.span 
              className="sds_chatbot-arrow"
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
            
            <motion.div 
              className="sds_chatbot-flow-item"
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <span className="sds_chatbot-flow-icon">❓</span>
              <p className="has-text-weight-bold">Ask Questions</p>
            </motion.div>
            
            <motion.span 
              className="sds_chatbot-arrow"
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            >
              →
            </motion.span>
            
            <motion.div 
              className="sds_chatbot-flow-item"
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <span className="sds_chatbot-flow-icon">📝</span>
              <p className="has-text-weight-bold">Collect Info</p>
            </motion.div>
            
            <motion.span 
              className="sds_chatbot-arrow"
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
            >
              →
            </motion.span>
            
            <motion.div 
              className="sds_chatbot-flow-item"
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <span className="sds_chatbot-flow-icon">🎯</span>
              <p className="has-text-weight-bold">Generate Lead</p>
            </motion.div>
          </div>
          
          <p className="has-text-centered mt-5 is-size-5">
            Simply drag and drop conversation elements to create your perfect chatbot in minutes!
          </p>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Lead Generation Flow Section
const LeadGenerationSection: React.FC = () => {
  const steps = [
    { icon: '👋', title: 'Visitor Arrives', desc: 'Chatbot greets website visitors instantly' },
    { icon: '💬', title: 'Conversation Starts', desc: 'Asks qualifying questions naturally' },
    { icon: '📋', title: 'Information Captured', desc: 'Collects contact details and preferences' },
    { icon: '🎯', title: 'Lead Generated', desc: 'Automatically adds to your CRM system' }
  ];

  return (
    <AnimatedSection className="sds_chatbot-leadgen section sds_chatbot-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_chatbot-section-title has-text-white">
            🎯 Smart Lead Generation
          </h2>
          <p className="subtitle is-5 has-text-white-ter">Based on intelligent conversations</p>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, index) => (
            <motion.div key={index} className="column" variants={scaleIn}>
              <motion.div 
                className="sds_chatbot-lead-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_chatbot-step-number">{index + 1}</div>
                <div className="sds_chatbot-step-icon">{step.icon}</div>
                <h4 className="title is-5 has-text-centered mb-3">{step.title}</h4>
                <p className="has-text-centered has-text-grey ">{step.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <div className="has-text-centered mt-5">
          <p className="is-size-4 has-text-white has-text-weight-semibold">
            Turn every website visitor into a potential customer!
          </p>
        </div>

        {/* Video Placeholder */}
        <div className="sds_chatbot-video-container mt-6">
          <motion.div 
            className="sds_chatbot-video-placeholder sds_chatbot-video-light"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">Lead Generation in Action</p>
            <p className="mt-2">Watch how chatbots capture and qualify leads</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Industry Use Cases Section
const IndustryUseCasesSection: React.FC = () => {
  const industries = [
    { 
      icon: '🎧', 
      title: 'Customer Service Excellence', 
      color: '#4CAF50',
      desc: 'Provide instant support 24/7. Answer questions, resolve issues, escalate to agents.',
      benefits: ['Answer FAQs instantly', 'Handle order inquiries', 'Process returns/exchanges', 'Collect customer feedback', 'Reduce tickets by 70%']
    },
    { 
      icon: '🛡️', 
      title: 'Insurance Claims & Support', 
      color: '#FF9800',
      desc: 'Guide customers through insurance processes, claims, and policy information.',
      benefits: ['Instant policy quotes', 'Claims filing assistance', 'Policy information lookup', 'Premium calculations', '24/7 emergency support']
    },
    { 
      icon: '🛒', 
      title: 'E-commerce Shopping Assistant', 
      color: '#9C27B0',
      desc: 'Help find products, compare options, and complete purchases with recommendations.',
      benefits: ['Product recommendations', 'Size and fit guidance', 'Order tracking', 'Abandoned cart recovery', 'Increase sales by 35%']
    },
    { 
      icon: '🏥', 
      title: 'Healthcare Appointment Booking', 
      color: '#F44336',
      desc: 'Schedule appointments, provide health info, and guide patients through processes.',
      benefits: ['Appointment scheduling', 'Symptom checker guidance', 'Prescription refill requests', 'Insurance verification', 'Reduce no-shows by 50%']
    },
    { 
      icon: '🏦', 
      title: 'Banking & Financial Services', 
      color: '#00BCD4',
      desc: 'Help with account inquiries, loan applications, and financial planning advice.',
      benefits: ['Account balance inquiries', 'Transaction history', 'Loan pre-qualification', 'Investment guidance', 'Fraud alert notifications']
    },
    { 
      icon: '🏠', 
      title: 'Real Estate Property Assistant', 
      color: '#8BC34A',
      desc: 'Help buyers find properties, schedule viewings, and provide market information.',
      benefits: ['Property search assistance', 'Schedule home viewings', 'Market value estimates', 'Mortgage calculator', 'Generate qualified leads']
    },
    { 
      icon: '🎓', 
      title: 'Educational Support', 
      color: '#673AB7',
      desc: 'Guide students through course selection, answer questions, support enrollment.',
      benefits: ['Course recommendations', 'Enrollment assistance', 'Academic calendar info', 'Assignment reminders', 'Student support services']
    },
    { 
      icon: '✈️', 
      title: 'Travel & Hospitality Concierge', 
      color: '#FF5722',
      desc: 'Assist with bookings, provide travel info, and offer personalized recommendations.',
      benefits: ['Hotel room bookings', 'Flight information', 'Local attraction guides', 'Restaurant recommendations', 'Check-in/out assistance']
    }
  ];

  return (
    <AnimatedSection className="sds_chatbot-industries section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_chatbot-section-title">How Different Industries Use Our Chatbots</h2>
          <p className="subtitle is-5">Real solutions for real business challenges</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {industries.map((industry, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div 
                className="sds_chatbot-industry-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderTopColor: industry.color }}
              >
                <div className="sds_chatbot-industry-icon" style={{ background: `linear-gradient(135deg, ${industry.color}, ${industry.color}dd)` }}>
                  {industry.icon}
                </div>
                <h3 className="title is-5 has-text-centered mb-3">{industry.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{industry.desc}</p>
                <ul className="sds_chatbot-benefits-list">
                  {industry.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="sds_chatbot-check-icon" style={{ background: industry.color }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Video Placeholder */}
        <div className="sds_chatbot-video-container mt-6">
          <motion.div 
            className="sds_chatbot-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">Industry Use Cases</p>
            <p className="mt-2">See chatbots in action across different industries</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Powerful Features Section
const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      icon: '🎯',
      title: 'Smart Lead Capture',
      color: '#2196F3',
      desc: 'Automatically identify and capture potential customers based on conversations.',
      benefits: ['Behavior-based lead scoring', 'Contact information collection', 'CRM integration', 'Follow-up automation', 'Conversion tracking']
    },
    {
      icon: '🌍',
      title: 'Multi-Language Support',
      color: '#4CAF50',
      desc: 'Communicate with customers in their preferred language for better engagement.',
      benefits: ['50+ languages supported', 'Auto language detection', 'Cultural context awareness', 'Regional customization', 'Translation accuracy 95%+']
    },
    {
      icon: '📊',
      title: 'Analytics & Insights',
      color: '#FF9800',
      desc: 'Get detailed reports on interactions, popular questions, and conversion rates.',
      benefits: ['Conversation analytics', 'Customer satisfaction scores', 'Popular topics tracking', 'Response time metrics', 'ROI measurement']
    }
  ];

  return (
    <AnimatedSection className="sds_chatbot-features section sds_chatbot-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_chatbot-section-title">Powerful Features That Make The Difference</h2>
          <p className="subtitle is-5">Everything you need for successful customer engagement</p>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column" variants={fadeInUp}>
              <motion.div 
                className="sds_chatbot-feature-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_chatbot-feature-icon" style={{ background: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="title is-4 has-text-centered mb-3">{feature.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{feature.desc}</p>
                <ul className="sds_chatbot-benefits-list">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="icon has-text-primary is-small">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Video Placeholder */}
        <div className="sds_chatbot-video-container mt-6">
          <motion.div 
            className="sds_chatbot-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">Feature Deep Dive</p>
            <p className="mt-2">Explore powerful chatbot capabilities</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_chatbot-cta section">
      <div className="container">
        <div className="box sds_chatbot-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">Ready to Transform Your Customer Experience?</h2>
              <p className="subtitle is-4 mb-6">
                Join thousands of businesses revolutionizing customer service with intelligent chatbots
              </p>
              
              <div className="sds_chatbot-stats mb-6">
                <div className="columns is-mobile is-multiline">
                  <div className="column is-4">
                    <motion.div 
                      className="sds_chatbot-stat"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="sds_chatbot-stat-number">24/7</p>
                      <p className="sds_chatbot-stat-label">Always Available</p>
                    </motion.div>
                  </div>
                  <div className="column is-4">
                    <motion.div 
                      className="sds_chatbot-stat"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="sds_chatbot-stat-number">70%</p>
                      <p className="sds_chatbot-stat-label">Cost Reduction</p>
                    </motion.div>
                  </div>
                  <div className="column is-4">
                    <motion.div 
                      className="sds_chatbot-stat"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="sds_chatbot-stat-number">3x</p>
                      <p className="sds_chatbot-stat-label">More Leads</p>
                    </motion.div>
                  </div>
                </div>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a 
                  href="tel:+91 9553896536" 
                  className="button is-primary is-large sds_chatbot-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a 
                  href="mailto:sds@hdtsolution.com" 
                  className="button is-info is-large sds_chatbot-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Get Free Demo</span>
                </motion.a>
              </div>

              {/* Video Placeholder */}
              <div className="sds_chatbot-video-container mt-6">
                <motion.div 
                  className="sds_chatbot-video-placeholder"
                  whileHover={{ scale: 1.02 }}
                >
                  <span className="icon is-large">
                    <i className="fas fa-play-circle fa-4x"></i>
                  </span>
                  <p className="has-text-weight-bold mt-4 is-size-4">Getting Started Guide</p>
                  <p className="mt-2">Quick demo on setting up your chatbot</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const ChatbotSolutions: React.FC = () => {
  return (
    <div className="sds_chatbot-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions - AI Chatbot & Customer Engagement Platform</h1>
        <meta name="description" content="Transform customer service with AI chatbots. 24/7 support, lead generation, multi-language support. Automate conversations and boost engagement." />
        <meta name="keywords" content="AI chatbot, customer service chatbot, lead generation, automated support, conversational AI, HDT Solutions" />
      </div>

      <HeroSection />
      <DragDropBuilderSection />
      <LeadGenerationSection />
      <IndustryUseCasesSection />
      <PowerfulFeaturesSection />
      <CTASection />
    </div>
  );
};

export default ChatbotSolutions;